import axios from 'axios';

import supabase from './supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

// Espejo del enum `user_role` de Postgres. Se duplica a proposito: el navegador y
// el Worker son bundles distintos y no comparten modulo. El arbitro real es la
// base, que rechaza cualquier valor fuera del enum.
export const ROLES = { VIEWER: 'viewer', ADMIN: 'admin' };

const encabezado = async () => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('No estas autenticado');
  return { Authorization: `Bearer ${token}` };
};

/**
 * Lee el rol del token.
 *
 * Antes esto era un SELECT a la tabla `users` en cada verificacion, y como
 * `ProtectedRoute` lo llama en cada ruta protegida, se pagaba una consulta por
 * navegacion. Ahora el rol viaja dentro del JWT como claim `user_role`, que lo
 * mete el Custom Access Token Hook al emitir el token.
 *
 * El payload se decodifica sin verificar la firma, y es a proposito. El
 * navegador no decide permisos: esto sirve para pintar la interfaz. Quien
 * decide son las politicas RLS y el Worker, que si verifican del lado del
 * servidor. Y el duenio de la sesion puede editar su propio localStorage de
 * todos modos, asi que verificar aca no protegeria de nadie: solo mostraria
 * botones que despues fallan al usarse.
 *
 * Antes esto llamaba a `getClaims()`. Como el proyecto firma con HS256, ese
 * metodo no puede verificar localmente y sale a `/auth/v1/user` por HTTP. Con
 * ProtectedRoute, Header y Admin preguntando por render, una sola carga de
 * /admin disparaba 15 llamadas a Supabase. Medido en la pestaña de red.
 *
 * Devuelve null si el usuario no tiene fila en `user_roles` o si el hook no
 * esta activo en el dashboard. Es lo primero que hay que mirar si despues de
 * desplegar nadie aparece como admin.
 */
const cuerpoDelToken = (jwt) => {
  const base64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const relleno = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const bytes = Uint8Array.from(atob(relleno), (c) => c.charCodeAt(0));
  // TextDecoder y no atob a secas: el payload trae el email, que puede tener
  // caracteres fuera de latin-1.
  return JSON.parse(new TextDecoder().decode(bytes));
};

export const rolActual = async (forzar = false) => {
  try {
    // El rol viaja dentro del token, asi que un cambio de rol no se nota hasta
    // que ese token se renueve —hasta una hora—. Renovar la sesion a pedido es
    // lo que hace que aplique en el momento.
    if (forzar) await supabase.auth.refreshSession();

    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) return null;

    return cuerpoDelToken(token).user_role ?? null;
  } catch {
    return null;
  }
};

/**
 * Mantiene la firma vieja —recibe `user`— porque ProtectedRoute y Header la
 * llaman asi, pero ya no usa el argumento: el rol sale del token de la sesion
 * activa, que es la del propio usuario.
 */
export const isAdmin = async (user) => {
  if (!user) return false;
  return (await rolActual()) === ROLES.ADMIN;
};

/**
 * Lista usuarios con su rol.
 *
 * Va por el Worker y no directo a Supabase: la politica RLS solo deja ver todos
 * los roles a quien tenga el permiso `users.manage`, y el Worker lo verifica
 * antes de consultar.
 */
export const obtenerUsuarios = async () => {
  const { data } = await axios.get(`${API_URL}/api/usuarios`, {
    headers: await encabezado(),
  });
  return data;
};

/**
 * Cambia el rol de un usuario.
 *
 * Esta es la parte que cierra el agujero. Antes era un
 * `supabase.from('users').update({ role })` desde el navegador con la anon key:
 * si las politicas de `users` permitian que cada uno actualizara su propia fila
 * —el patron mas comun—, cualquiera se hacia admin desde la consola.
 *
 * Ahora `user_roles` no tiene ninguna politica de escritura, asi que ni la anon
 * key ni el token del usuario pueden tocarla. La unica via es la service key,
 * que vive como secret en el Worker y nunca llega al navegador.
 */
export const cambiarRol = async (userId, rol) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/api/usuarios/${userId}/rol`,
      { rol },
      { headers: await encabezado() },
    );
    return { success: true, message: data.aviso, data };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

/** Promueve por email. Mantiene la firma porque AdminTools la llama asi. */
export const promoteToAdmin = async (email) => {
  try {
    const usuarios = await obtenerUsuarios();
    const objetivo = usuarios.find(
      (u) => u.email?.toLowerCase() === email.trim().toLowerCase(),
    );

    if (!objetivo) {
      return {
        success: false,
        message: `No se encontro ningun usuario con el email ${email}`,
      };
    }

    const resultado = await cambiarRol(objetivo.id, ROLES.ADMIN);
    if (!resultado.success) return resultado;

    return {
      success: true,
      message: `${email} ahora es administrador. ${resultado.message}`,
    };
  } catch (error) {
    return {
      success: false,
      message: `Error al promover usuario: ${error.response?.data?.message || error.message}`,
    };
  }
};

/**
 * Crea un usuario y le asigna un rol.
 *
 * El alta en Auth sigue saliendo del navegador —es el mismo signUp de siempre—
 * pero el rol se asigna en un segundo paso contra el Worker. Si ese paso falla,
 * el usuario queda creado sin rol: se avisa en vez de fingir que salio bien,
 * porque sin rol no tiene permisos y hay que reintentarlo.
 *
 * Los argumentos van posicionales porque asi la llama AdminTools. La version
 * anterior declaraba `createUser(userData)` esperando un objeto, pero recibia
 * cuatro sueltos: `userData` terminaba siendo el string del email y
 * `userData.email` daba undefined. El alta de usuarios no funcionaba.
 */
export const createUser = async (email, password, role = ROLES.VIEWER, username = '') => {
  try {
    const { data: alta, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username: username || email.split('@')[0] } },
    });

    if (error) throw error;
    if (!alta.user) {
      return { success: false, message: 'No se pudo crear el usuario en Auth' };
    }

    const asignado = await cambiarRol(alta.user.id, role);
    if (!asignado.success) {
      return {
        success: false,
        message: `Usuario ${email} creado, pero no se le pudo asignar el rol: ${asignado.message}`,
      };
    }

    return {
      success: true,
      message: `Usuario ${email} creado con rol ${role}`,
      user: alta.user,
    };
  } catch (error) {
    return { success: false, message: `Error al crear usuario: ${error.message}` };
  }
};

const adminUtils = {
  ROLES,
  rolActual,
  isAdmin,
  obtenerUsuarios,
  cambiarRol,
  promoteToAdmin,
  createUser,
};

export default adminUtils;
